import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/i18n";

const IMAGES = [
  {
    src: "https://images.unsplash.com/photo-1592671520190-e41aa6c959e6?auto=format&fit=crop&w=1200&q=80",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]"
  },
  {
    src: "https://images.unsplash.com/photo-1739771783076-d68d8b7d50ab?auto=format&fit=crop&w=1000&q=80",
    span: "lg:col-span-5",
    aspect: "aspect-[16/10] lg:aspect-[4/3.4]"
  },
  {
    src: "https://images.unsplash.com/photo-1600185263071-89deb983edd8?auto=format&fit=crop&w=1000&q=80",
    span: "lg:col-span-5",
    aspect: "aspect-[16/10] lg:aspect-[4/3.4]"
  },
  {
    src: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/cb/Sestri_Levante_and_Baia_del_Silenzio%2C_the_Bay_of_Silence.jpg/1280px-Sestri_Levante_and_Baia_del_Silenzio%2C_the_Bay_of_Silence.jpg",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]"
  }
];

export default function Territory() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const drift = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="territorio" ref={ref} data-testid="territory-section" className="bg-ivory py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <Reveal>
          <Overline>{t.territory.overline}</Overline>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <h2 className="max-w-2xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-navy md:text-5xl lg:text-6xl">
              {t.territory.title}
            </h2>
            <p className="max-w-md text-sm font-light leading-relaxed text-navy/60 md:text-base">{t.territory.text}</p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 md:mt-20 lg:grid-cols-12 lg:gap-8">
          {IMAGES.map((img, i) => (
            <Reveal key={img.src} delay={i * 0.08} className={img.span}>
              <figure data-testid={`territory-image-${i + 1}`} className="group">
                <div className={`overflow-hidden ${img.aspect}`}>
                  <motion.img
                    style={i % 2 === 0 ? { y: drift } : undefined}
                    src={img.src}
                    alt={t.territory.captions[i]}
                    loading="lazy"
                    className="h-full w-full scale-110 object-cover transition-transform duration-700 ease-out group-hover:scale-115"
                  />
                </div>
                <figcaption className="mt-3 flex items-center gap-3 font-serif text-lg italic text-navy/70">
                  <span className="inline-block h-1.5 w-1.5 rotate-45 bg-champagne" />
                  {t.territory.captions[i]}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-14">
          <div className="flex flex-wrap gap-3" data-testid="territory-locations">
            {(t.marquee || []).map((place) => (
              <span
                key={place}
                className="rounded-full border border-navy/20 px-5 py-2 text-[11px] font-medium uppercase tracking-[0.22em] text-navy/70 transition-colors duration-300 hover:border-champagne hover:text-champagne"
              >
                {place}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
